import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';
import { DialogValidationErrors } from '@/components/forms/DialogValidationErrors';
import { ValidationError, getApiErrorMessage, getValidationErrors } from '@/lib/validationErrors';

interface CopiedVersion {
  id: string;
  versionNo: number;
  label?: string | null;
}

interface CopyPlanVersionDialogProps {
  /** The SOURCE plan version to copy from. */
  entityId: string;
  open: boolean;
  onClose: () => void;
  /** Called after a successful copy (the hub uses it to re-evaluate). */
  onCopied?: (newVersion: CopiedVersion) => void;
}

/**
 * Start the next round: POST /api/finance/planversion/{id}/copyPlanVersion — a new DRAFT version
 * copying the header structure (never values), with the choice between the full structure and a
 * one-header-per-variant skeleton to fan out across the org tree.
 * Spec: docs/15-designspecs/CopyPlanVersion.md.
 */
export function CopyPlanVersionDialog({ entityId, open, onClose, onCopied }: CopyPlanVersionDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [label, setLabel] = useState('');
  const [mode, setMode] = useState<'full' | 'variant'>('full');
  const [created, setCreated] = useState<CopiedVersion | null>(null);
  const [apiValidationErrors, setApiValidationErrors] = useState<ValidationError[]>([]);

  // One key per dialog session: a network retry of the same attempt replays instead of copying twice.
  const idempotencyKeyRef = useRef<string>(crypto.randomUUID());

  const copyMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<CopiedVersion>(
        `/api/finance/planversion/${entityId}/copyPlanVersion`,
        { label: label.trim() || null, oneHeaderPerVariant: mode === 'variant' },
        { headers: { 'X-Idempotency-Key': idempotencyKeyRef.current } },
      );
      return response.data;
    },
    onSuccess: (version) => {
      setCreated(version);
      setApiValidationErrors([]);
      onCopied?.(version);
    },
    onError: (error: unknown) => {
      const errors = getValidationErrors(error);
      if (errors.length > 0) {
        setApiValidationErrors(errors);
        return;
      }
      // Not a field-level validation failure: surface the server's own message (business
      // exceptions, 500s) before falling back to a generic text.
      const message = getApiErrorMessage(error)
        ?? t('common.genericError', { defaultValue: 'An error occurred' });
      setApiValidationErrors([{ field: 'form', message, instancePath: '#', members: [] }]);
    },
  });

  const handleClose = () => {
    setCreated(null);
    setLabel('');
    setMode('full');
    setApiValidationErrors([]);
    idempotencyKeyRef.current = crypto.randomUUID();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('Finance.CopyPlanVersion.Title', { defaultValue: 'New version (copy structure)' })}
      </DialogTitle>
      <DialogContent>
        <DialogValidationErrors
          errors={apiValidationErrors}
          onClear={() => setApiValidationErrors([])}
        />

        {created ? (
          <Alert
            severity="success"
            action={
              <Button
                size="small"
                onClick={() => { handleClose(); navigate(`/finance/planversions/${created.id}`); }}
              >
                {t('Finance.CopyPlanVersion.OpenNew', { defaultValue: 'Open version' })}
              </Button>
            }
          >
            {t('Finance.CopyPlanVersion.Success', {
              defaultValue: 'Draft V{{versionNo}} created — structure copied, no values.',
              versionNo: created.versionNo,
            })}
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('Finance.CopyPlanVersion.Intro', {
                defaultValue: 'Creates a new draft version in the same scenario. Header structure, owners and capacities follow — values never do.',
              })}
            </Typography>
            <TextField
              fullWidth
              autoFocus
              label={t('Finance.CopyPlanVersion.LabelField', {
                defaultValue: 'Label (optional — defaults to the version number, e.g. V5)',
              })}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              sx={{ mb: 2 }}
            />
            <FormControl>
              <RadioGroup value={mode} onChange={(e) => setMode(e.target.value as 'full' | 'variant')}>
                <FormControlLabel
                  value="full"
                  control={<Radio />}
                  label={t('Finance.CopyPlanVersion.ModeFull', {
                    defaultValue: 'All headers — the full structure for the whole org tree',
                  })}
                />
                <FormControlLabel
                  value="variant"
                  control={<Radio />}
                  label={t('Finance.CopyPlanVersion.ModeVariant', {
                    defaultValue: 'One header per header type and plan view — a skeleton to fan out afterwards',
                  })}
                />
              </RadioGroup>
            </FormControl>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          {created
            ? t('common.close', { defaultValue: 'Close' })
            : t('common.cancel', { defaultValue: 'Cancel' })}
        </Button>
        {!created && (
          <Button
            variant="contained"
            disabled={copyMutation.isPending}
            onClick={() => copyMutation.mutate()}
          >
            {t('Finance.CopyPlanVersion.Confirm', { defaultValue: 'Create draft' })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
